import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ContactlessSignInResult, InductionAttemptResult, InductionToTake, ContactlessComplianceSection, KioskNoticesStepConfig } from '@cloudsign/shared';
import type { ContactlessConfig } from '@cloudsign/shared';
import { z } from 'zod';
import { t } from '../../lib/i18n';
import { Alert, Button, CheckboxField, TextField } from '../../components/ui';
import { InductionTaker } from './InductionTaker';
import type { AttemptInput } from './InductionTaker';
import { HostPicker, NoticesStep, PhotoStep, QuestionsStep, askableFields } from './steps';
import { call, problemText } from './api';
import { newKey } from './util';

type Group = ContactlessConfig['groups'][number];
export type Remembered = { fullName: string; company: string; email: string } | null;

/** The pages of one sign-in: details first, then each step the group has that a phone can show. */
type Page = { key: 'details' } | { key: 'questions' | 'photo'; config: unknown } | { key: 'notices'; config: KioskNoticesStepConfig } | { key: 'induction' };

/** True when the phone's compliance section (groups and their induction ids only) says this group has an induction to pass. */
export function groupHasInduction(config: ContactlessConfig, groupId: string): boolean {
  const s = ContactlessComplianceSection.safeParse(config.sections['compliance']);
  return s.success && s.data.groups.some((g) => g.groupId === groupId && g.inductionId !== null);
}

export function pagesFor(config: ContactlessConfig, group: Group): Page[] {
  const pages: Page[] = [{ key: 'details' }];
  for (const s of [...group.signInSteps].sort((a, b) => a.order - b.order)) {
    if (s.key === 'questions') {
      if (askableFields(s.config as { fields?: unknown[] }).length > 0) pages.push({ key: 'questions', config: s.config });
    } else if (s.key === 'photo') {
      pages.push({ key: 'photo', config: s.config });
    } else if (s.key === 'notices') {
      const n = KioskNoticesStepConfig.safeParse(s.config);
      if (n.success && n.data.notices.length > 0) pages.push({ key: 'notices', config: n.data });
    }
    // Other steps (badge, permits, ...) are not shown on a phone: reception deals with them.
  }
  if (groupHasInduction(config, group.id)) pages.push({ key: 'induction' });
  return pages;
}

const BIG = 'min-h-12 w-full text-base';
const Attempts = z.array(InductionToTake);

/** Induction step: the server lists what this visitor still has to pass; nothing to take means the step is a plain "Continue". */
function InductionPage({ session, groupId, name, email, nextLabel, onNext }: { session: string; groupId: string | null; name: string; email: string; nextLabel: string; onNext: () => void }) {
  const q = useQuery({
    queryKey: ['contactless', 'inductions', session, groupId],
    queryFn: () => call('GET', '/compliance/contactless/inductions', { session, ...(groupId ? { query: { groupId } } : {}), schema: Attempts }),
    retry: false,
  });
  const [allPassed, setAllPassed] = useState(false);
  const items = q.data ?? [];
  const submit = (a: AttemptInput) =>
    call('POST', '/compliance/contactless/inductions/attempts', { session, body: { ...a, visitorName: name, email: email || null }, schema: InductionAttemptResult });
  if (q.isPending) return <p role="status">{t('w21.loading')}</p>;
  return (
    <div className="space-y-4">
      {items.length > 0 ? <InductionTaker items={items} submit={submit} onAllPassed={() => setAllPassed(true)} /> : null}
      {q.isError ? <Alert kind="info">{t('w21.induction.unavailable')}</Alert> : null}
      <Button variant="primary" className={BIG} disabled={items.length > 0 && !allPassed} onClick={onNext}>
        {nextLabel}
      </Button>
    </div>
  );
}

export interface SignedIn {
  result: ContactlessSignInResult;
  groupName: string;
}

/**
 * The sign-in wizard: group, details, then the group's steps one screen at a time. Camera is only asked for on the photo step
 * (the browser asks when the visitor taps the button), location never here. One idempotency key per attempt, so a retry after
 * a lost connection cannot sign anyone in twice.
 */
export function SignInFlow({
  config,
  session,
  remembered,
  hasRememberToken,
  onForget,
  onDone,
}: {
  config: ContactlessConfig;
  session: string;
  remembered: Remembered;
  hasRememberToken: boolean;
  onForget: () => void;
  onDone: (s: SignedIn) => void;
}) {
  const [groupId, setGroupId] = useState<string | null>(config.groups.length === 1 ? (config.groups[0]?.id ?? null) : null);
  const [index, setIndex] = useState(0);
  const [name, setName] = useState(remembered?.fullName ?? '');
  const [company, setCompany] = useState(remembered?.company ?? '');
  const [email, setEmail] = useState(remembered?.email ?? '');
  const [host, setHost] = useState<{ id: string; name: string } | null>(null);
  const [remember, setRemember] = useState(remembered !== null);
  const [steps, setSteps] = useState<Record<string, unknown>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [key] = useState(newKey);

  const group = config.groups.find((g) => g.id === groupId) ?? null;
  if (config.groups.length === 0) return <Alert kind="info">{t('w21.noGroups')}</Alert>;
  if (!group) {
    return (
      <fieldset className="space-y-3">
        <legend className="mb-2 text-xl font-semibold">{t('w21.group.title')}</legend>
        {config.groups.map((g) => (
          <Button key={g.id} variant="secondary" className="min-h-14 w-full justify-start text-lg" onClick={() => setGroupId(g.id)}>
            {g.name}
          </Button>
        ))}
      </fieldset>
    );
  }

  const pages = pagesFor(config, group);
  const page = pages[Math.min(index, pages.length - 1)] as Page;
  const last = index >= pages.length - 1;
  const nextLabel = last ? t('w21.signIn.button') : t('w21.next');

  const submit = async (all: Record<string, unknown>) => {
    setBusy(true);
    setProblem(null);
    try {
      const result = await call('POST', '/contactless/session/sign-in', {
        session,
        schema: ContactlessSignInResult,
        body: {
          idempotencyKey: key,
          groupId: group.id,
          visitorName: name.trim(),
          email: email.trim() === '' ? null : email.trim(),
          company: company.trim(),
          hostMemberId: host?.id ?? null,
          steps: all,
          remember,
        },
      });
      onDone({ result, groupName: group.name });
    } catch (e) {
      setProblem(problemText(e));
    } finally {
      setBusy(false);
    }
  };

  // Moves on, or signs in when this was the last screen.
  const advance = (stepKey: string | null, value: unknown) => {
    const all = stepKey && value !== null && value !== undefined ? { ...steps, [stepKey]: value } : steps;
    setSteps(all);
    if (last) void submit(all);
    else setIndex((i) => i + 1);
  };

  const details = () => {
    const bad: Record<string, string> = {};
    if (name.trim() === '') bad['name'] = t('w21.details.nameRequired');
    else if (group.requireFullName && name.trim().split(/\s+/).length < 2) bad['name'] = t('w21.details.fullNameRequired');
    if (email.trim() !== '' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) bad['email'] = t('w21.details.emailInvalid');
    setErrors(bad);
    if (Object.keys(bad).length === 0) advance(null, null);
  };

  return (
    <div className="space-y-4">
      {group.welcomeMessage ? <p className="text-base text-slate-800">{group.welcomeMessage}</p> : null}
      {pages.length > 1 ? <p className="text-sm text-slate-700">{t('w21.stepOf', { n: index + 1, total: pages.length })}</p> : null}
      {page.key === 'details' ? (
        <div className="space-y-4">
          <h2 className="text-xl font-semibold">{t('w21.details.title')}</h2>
          <TextField label={t('w21.details.name')} value={name} error={errors['name']} autoComplete="name" maxLength={120} onChange={(e) => setName(e.target.value)} />
          <TextField label={t('w21.details.company')} value={company} autoComplete="organization" maxLength={120} onChange={(e) => setCompany(e.target.value)} />
          <TextField label={t('w21.details.email')} hint={t('w21.details.emailHint')} type="email" inputMode="email" value={email} error={errors['email']} autoComplete="email" onChange={(e) => setEmail(e.target.value)} />
          <HostPicker session={session} value={host} onChange={setHost} />
          <CheckboxField label={t('w21.remember.label')} hint={t('w21.remember.hint')} checked={remember} onChange={(e) => setRemember(e.target.checked)} />
          {hasRememberToken ? (
            <button type="button" className="min-h-11 text-base text-brand underline" onClick={onForget}>
              {t('w21.forget.button')}
            </button>
          ) : null}
          {problem && last ? <Alert>{problem}</Alert> : null}
          <Button variant="primary" className={BIG} disabled={busy} onClick={details}>
            {nextLabel}
          </Button>
        </div>
      ) : null}
      {page.key === 'questions' ? <QuestionsStep config={page.config as { fields?: unknown[] }} session={session} busy={busy} nextLabel={nextLabel} onNext={(v) => advance('questions', v)} /> : null}
      {page.key === 'notices' ? <NoticesStep config={page.config} session={session} busy={busy} nextLabel={nextLabel} onNext={(v) => advance('notices', v)} /> : null}
      {page.key === 'photo' ? <PhotoStep config={page.config as { mode?: 'always' | 'chance' }} session={session} busy={busy} nextLabel={nextLabel} onNext={(v) => advance('photo', v)} /> : null}
      {page.key === 'induction' ? <InductionPage session={session} groupId={groupId} name={name.trim()} email={email.trim()} nextLabel={nextLabel} onNext={() => advance(null, null)} /> : null}
      {problem && !(page.key === 'details' && last) ? <Alert>{problem}</Alert> : null}
      {index > 0 && !busy ? (
        <button type="button" className="min-h-11 text-base text-slate-700 underline" onClick={() => setIndex((i) => Math.max(0, i - 1))}>
          {t('w21.back')}
        </button>
      ) : null}
    </div>
  );
}
